import { useEffect, useState } from "react";
import {
  Create,
  Dns,
  Input,
  QrCodeOutlined,
  ErrorOutline,
  ShoppingCart,
} from "@mui/icons-material";
import {
  Button,
  Stack,
  Typography,
  Paper,
  Box,
  alpha,
  Divider,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ScanQRCodeDialog from "./ScanQR";
import ClaimCodeDialog from "./claimCode";
import SelfHostDialog from "./selfHost";
import ManualKeysDialog from "./manualKeys";
import { CONFIG } from "../../config";
import { useSelector } from "react-redux";
import DialogBase from "./DialogBase";
import { OrganisationService } from "../../services/OrganisationService";

export default function AddDeviceDialog(props) {
  const [openQrScanner, setOpenQrScanner] = useState(false);
  const [openManual, setOpenManual] = useState(false);
  const [openSelfHost, setOpenSelfHost] = useState(false);
  const [openClaimCode, setOpenClaimCode] = useState(false);
  const theme = useTheme();

  const selectedOrganisation = useSelector(
    (state) => state.selectedOrganisation
  );
  const user = useSelector((state) => state.user);

  const [limitReached, setLimitReached] = useState(false);

  useEffect(() => {
    if (CONFIG.billing_enabled) {
      if (!selectedOrganisation) {
        return;
      }
      if (!selectedOrganisation.id) {
        return;
      }

      async function fetchData(organisationId) {
        let limit =
          await OrganisationService.fetchRemainingDeviceLimit(organisationId);

        if (limit <= 0) {
          setLimitReached(true);
        } else {
          setLimitReached(false);
        }
      }

      fetchData(selectedOrganisation.id);
    }
  }, [selectedOrganisation]);

  const { t } = useTranslation("common");

  const { open, handleClose } = props;

  const handleQrScannerClose = () => {
    setOpenQrScanner(false);
  };

  const handleManualClose = () => {
    setOpenManual(false);
  };

  const handleClaimCodeClose = () => {
    setOpenClaimCode(false);
  };

  const handleSelfHostClose = () => {
    setOpenSelfHost(false);
  };

  return (
    <DialogBase
      open={open}
      handleClose={handleClose}
      title={t("devices.add_device_title")}
      actions={
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 500,
            color:
              theme.palette.mode === "dark"
                ? "#00c9a7"
                : theme.palette.secondary.main,
            borderColor: alpha(
              theme.palette.mode === "dark"
                ? "#00c9a7"
                : theme.palette.secondary.main,
              0.5
            ),
            "&:hover": {
              borderColor:
                theme.palette.mode === "dark"
                  ? "#00c9a7"
                  : theme.palette.secondary.main,
              backgroundColor: alpha(
                theme.palette.mode === "dark"
                  ? "#00c9a7"
                  : theme.palette.secondary.main,
                0.05
              ),
            },
          }}
        >
          {t("common.cancel")}
        </Button>
      }
    >
      {limitReached &&
      selectedOrganisation &&
      selectedOrganisation.owner_id !== user.id ? (
        <Box sx={{ p: 3 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "12px",
              backgroundColor: alpha(theme.palette.error.main, 0.05),
              borderLeft: `4px solid ${theme.palette.error.main}`,
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <ErrorOutline fontSize="large" color="error" sx={{ mt: 0.5 }} />
              <Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Device Limit Reached
                </Typography>
                <Typography>
                  Please ask the organisation owner to increase their device
                  limit.
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      ) : (
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => setOpenQrScanner(true)}
              startIcon={<QrCodeOutlined />}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                py: 1.5,
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "#00c9a7"
                    : theme.palette.primary.main,
                color: theme.palette.mode === "dark" ? "#000" : "#fff",
                fontWeight: 500,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                "&:hover": {
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? alpha("#00c9a7", 0.9)
                      : alpha(theme.palette.primary.main, 0.9),
                  boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              {t("devices.scan_qr_code")}
            </Button>

            <Button
              variant="contained"
              fullWidth
              onClick={() => setOpenClaimCode(true)}
              startIcon={<Input />}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                py: 1.5,
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "#00c9a7"
                    : theme.palette.primary.main,
                color: theme.palette.mode === "dark" ? "#000" : "#fff",
                fontWeight: 500,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                "&:hover": {
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? alpha("#00c9a7", 0.9)
                      : alpha(theme.palette.primary.main, 0.9),
                  boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              {t("devices.enter_claim_code")}
            </Button>

            {CONFIG.self_host && (
              <Button
                variant="contained"
                fullWidth
                onClick={() => setOpenSelfHost(true)}
                startIcon={<Dns />}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  py: 1.5,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "#00c9a7"
                      : theme.palette.primary.main,
                  color: theme.palette.mode === "dark" ? "#000" : "#fff",
                  fontWeight: 500,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  "&:hover": {
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? alpha("#00c9a7", 0.9)
                        : alpha(theme.palette.primary.main, 0.9),
                    boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                {t("devices.self_host")}
              </Button>
            )}

            {CONFIG.manual_add && (
              <Button
                variant="contained"
                fullWidth
                onClick={() => setOpenManual(true)}
                startIcon={<Create />}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  py: 1.5,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "#00c9a7"
                      : theme.palette.primary.main,
                  color: theme.palette.mode === "dark" ? "#000" : "#fff",
                  fontWeight: 500,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  "&:hover": {
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? alpha("#00c9a7", 0.9)
                        : alpha(theme.palette.primary.main, 0.9),
                    boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                {t("devices.enter_manually")}
              </Button>
            )}
          </Stack>

          {/* Buy Device Panel - only shown if feature is enabled */}
          {CONFIG.show_buy_link && CONFIG.buy_link && (
            <>
              <Divider sx={{ my: 3 }} />
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "12px",
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  borderLeft: `4px solid ${theme.palette.primary.main}`,
                  textAlign: "center",
                }}
              >
                <Box sx={{ mb: 2 }}>
                  <ShoppingCart
                    fontSize="large"
                    color="primary"
                    sx={{ mb: 1, opacity: 0.8 }}
                  />
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    gutterBottom
                    sx={{ color: theme.palette.text.primary }}
                  >
                    {t("devices.dont_own_device")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {t("devices.buy_device_description")}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  onClick={() => window.open(CONFIG.buy_link, "_blank")}
                  startIcon={<ShoppingCart />}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    py: 1,
                    px: 3,
                    fontWeight: 500,
                    color: theme.palette.primary.main,
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      transform: "translateY(-1px)",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  {t("devices.buy_device")}
                </Button>
              </Paper>
            </>
          )}
        </Box>
      )}

      <Divider />

      <ScanQRCodeDialog
        open={openQrScanner}
        handleClose={handleQrScannerClose}
      />
      <ClaimCodeDialog
        open={openClaimCode}
        handleClose={handleClaimCodeClose}
      />
      <SelfHostDialog open={openSelfHost} handleClose={handleSelfHostClose} />
      <ManualKeysDialog open={openManual} handleClose={handleManualClose} />
    </DialogBase>
  );
}
